import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, AlertCircle, Calendar, Sparkles, Compass, BookOpen } from 'lucide-react';

const STORAGE_KEY = 'numenaxis_birthdate_info_seen';

export default function BirthDateInfoModal({ isOpen, onClose, onConfirm }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay for animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    // Save to localStorage that user has seen this modal
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
    setTimeout(() => {
      // Use onConfirm if provided, otherwise fallback to onClose
      if (onConfirm && typeof onConfirm === 'function') {
        onConfirm();
      } else if (onClose && typeof onClose === 'function') {
        onClose();
      }
    }, 200);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Card className={`relative w-full max-w-2xl bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 border-2 border-amber-500/30 shadow-glow max-h-[90vh] overflow-y-auto transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 p-6 pb-4 border-b border-amber-500/20 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center border border-amber-500/50">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="font-display text-xl text-amber-400">
              📌 Svarbi pastaba prieš įvedant gimimo datą
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-foreground/90">
          {/* Intro */}
          <p className="text-sm leading-relaxed">
            NUMENAXIS naudoja šiuolaikinį psichomatricos skaičiavimo metodą, kuris atsižvelgia į laikmečio energetinius pokyčius.
          </p>

          {/* Section: How to enter */}
          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
            <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Kaip teisingai įvesti gimimo datą:
            </h3>

            {/* Before 1999 */}
            <div className="mb-4">
              <h4 className="font-medium text-purple-400 mb-2">🔹 Jei gimei iki 1999 m. (imtinai)</h4>
              <p className="text-sm text-foreground/80 mb-2">
                Jeigu diena arba mėnuo yra nuo 1 iki 9, juos vesk be nulio priekyje.
              </p>
              <div className="bg-slate-900/50 rounded p-3 text-sm">
                <p className="mb-1">Pavyzdys:</p>
                <p className="text-green-400">✔️ 7.9.1975</p>
                <p className="text-red-400">❌ 07.09.1975</p>
              </div>
              <p className="text-sm text-foreground/70 mt-2 italic">
                Jeigu diena ar mėnuo yra 10, 20, 30 – nuliai rašomi įprastai.
              </p>
            </div>

            {/* After 2000 */}
            <div>
              <h4 className="font-medium text-purple-400 mb-2">🔹 Jei gimei 2000 m. arba vėliau</h4>
              <p className="text-sm text-foreground/80 mb-2">
                Gimimo datą vesk tiksliai taip, kaip ji rašoma oficialiai, su nuliais, jei jie yra.
              </p>
              <div className="bg-slate-900/50 rounded p-3 text-sm">
                <p className="mb-1">Pavyzdys:</p>
                <p className="text-green-400">✔️ 07.09.2005</p>
                <p className="text-green-400">✔️ 09.08.2001</p>
              </div>
            </div>
          </div>

          {/* Section: Why */}
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
            <h3 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
              🌍 Kodėl taip yra?
            </h3>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Po 2000 metų pasikeitė ne tik kalendorius, bet ir informacinė bei energetinė žmogaus struktūra.
              Gimimo datose atsiradę nuliai tapo aktyviais informacijos nešėjais, todėl ir numerologiniai skaičiavimai šiuolaikiniame kontekste šiek tiek pasikeitė.
            </p>
          </div>

          {/* Section: For curious */}
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Smalsiems ir ieškotojams
            </h3>
            <p className="text-sm text-foreground/80 leading-relaxed mb-2">
              Jei nori – išbandyk abu variantus (su nuliu ir be jo) ir stebėk, kuris aprašymas labiau apie tave.
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Skirtumas dažniausiai nebus radikalus, tačiau jis gali subtiliai pakeisti akcentus.
              <span className="text-amber-400 font-medium"> Teisingesnis visada yra tas variantas, kuris tau labiau rezonuoja.</span>
            </p>
          </div>

          {/* Section: Important to know */}
          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
            <h3 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
              <Compass className="w-4 h-4" />
              Svarbu žinoti
            </h3>
            <p className="text-sm text-foreground/80 leading-relaxed mb-2">
              Pirmą kartą įvesta gimimo data išsaugoma sistemoje.
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed mb-2">
              Jeigu tampi VIP nariu, asmeninė PDF Sielos Knyga bus paruošta pagal šią pradinę datą.
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Vėliau tu visada galėsi:
            </p>
            <ul className="text-sm text-foreground/80 ml-4 mt-1 space-y-1">
              <li>– pasitikrinti skaičiavimus ir kitu būdu,</li>
              <li>– palyginti rezultatus savo smalsumui ir savęs pažinimui.</li>
            </ul>
          </div>
        </div>

        {/* Footer with button */}
        <div className="sticky bottom-0 bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 p-6 pt-4 border-t border-amber-500/20">
          <Button
            onClick={handleConfirm}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6 text-lg glow-gold transition-all duration-300"
            data-testid="birthdate-info-confirm-btn"
          >
            Supratau
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Helper function to check if user has seen the modal
export function hasSeenBirthDateInfo() {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

// Helper function to mark the modal as seen
export function markBirthDateInfoSeen() {
  localStorage.setItem(STORAGE_KEY, 'true');
}

// Helper function to reset the modal state (for testing)
export function resetBirthDateInfoState() {
  localStorage.removeItem(STORAGE_KEY);
}
